import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import { Plus, Edit, Trash2, CreditCard, ArrowLeft, Wallet, Lightbulb, Building2, PiggyBank, Landmark, Banknote, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;

const CORES_DISPONIVEIS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#06B6D4', '#6366F1', '#14B8A6', '#F97316'
];

const TIPOS_CONTA = [
  { value: 'conta_corrente', label: 'Conta Corrente', Icon: Landmark },
  { value: 'poupanca', label: 'Poupança', Icon: PiggyBank },
  { value: 'cartao_credito', label: 'Cartão de Crédito', Icon: CreditCard },
  { value: 'cartao_debito', label: 'Cartão de Débito', Icon: CreditCard },
  { value: 'dinheiro', label: 'Dinheiro', Icon: Banknote },
  { value: 'investimento', label: 'Investimento', Icon: TrendingUp }
];

const ContasView = ({ onVoltar }) => {
  const { theme: themeColors, isDarkMode } = useTheme();
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'conta_corrente',
    saldo_inicial: '0,00',
    cor: '#3B82F6',
    icone: '',
    limite_credito: '0,00',
    dia_fechamento: null,
    dia_vencimento: null
  });

  // Função para formatar valor como moeda brasileira (input)
  const formatInputCurrency = (value) => {
    // Remove tudo que não é dígito
    const digits = value.replace(/\D/g, '');
    
    if (!digits) return '';
    
    // Converte para número e divide por 100 (centavos)
    const number = parseInt(digits) / 100;
    
    // Formata com separadores brasileiros
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter valor formatado de volta para float
  const parseCurrencyToFloat = (value) => {
    if (!value) return 0;
    
    // Remove R$, espaços, pontos (separador de milhar)
    // Troca vírgula (separador decimal) por ponto
    const cleanValue = value
      .replace(/R\$/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(/,/g, '.');
    
    return parseFloat(cleanValue) || 0;
  };

  // Handler para o campo de saldo inicial
  const handleSaldoInicialChange = (e) => {
    const inputValue = e.target.value;
    const formatted = formatInputCurrency(inputValue);
    setFormData({ ...formData, saldo_inicial: formatted });
  };

  // Handler para o campo de limite de crédito
  const handleLimiteCreditoChange = (e) => {
    const inputValue = e.target.value;
    const formatted = formatInputCurrency(inputValue);
    setFormData({ ...formData, limite_credito: formatted });
  };

  useEffect(() => {
    loadContas();
  }, []);

  // Fechar modal ao pressionar ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConta(null);
    resetForm();
  };

  const loadContas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financeiro/contas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContas(response.data.contas || []);
    } catch (error) {
      console.error('Error loading contas:', error);
      toast.error('Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Converter valores formatados para float
      const saldoInicialFloat = parseCurrencyToFloat(formData.saldo_inicial);
      const limiteCreditoFloat = formData.tipo === 'cartao_credito' 
        ? parseCurrencyToFloat(formData.limite_credito) 
        : null;
      
      const payload = {
        ...formData,
        saldo_inicial: saldoInicialFloat,
        limite_credito: limiteCreditoFloat
      };
      
      if (editingConta) {
        await axios.put(`${API}/financeiro/contas/${editingConta.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Conta atualizada!');
      } else {
        // Criar conta
        const response = await axios.post(`${API}/financeiro/contas`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Se tem saldo inicial e não é cartão de crédito, criar transação automática
        if (saldoInicialFloat > 0 && formData.tipo !== 'cartao_credito') {
          const contaCriada = response.data.conta;
          
          try {
            await axios.post(`${API}/financeiro/receitas`, {
              data: new Date().toISOString().split('T')[0],
              descricao: `Saldo Inicial - ${formData.nome}`,
              valor: saldoInicialFloat,
              categoria_id: null, // Sem categoria para saldo inicial
              conta_id: contaCriada.id,
              recebido: true
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            console.error('Erro ao criar transação de saldo inicial:', err);
          }
        }
        
        toast.success('Conta criada!');
      }
      
      setShowModal(false);
      setEditingConta(null);
      resetForm();
      loadContas();
    } catch (error) {
      console.error('Error saving conta:', error);
      toast.error('Erro ao salvar conta');
    }
  };

  const handleDelete = async (conta) => {
    if (!window.confirm(`Excluir "${conta.nome}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/financeiro/contas/${conta.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conta excluída!');
      loadContas();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erro ao excluir conta');
    }
  };

  const openEditModal = (conta) => {
    setEditingConta(conta);
    
    // Formatar valores para exibição
    const saldoFormatado = (conta.saldo_inicial || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    const limiteFormatado = (conta.limite_credito || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    setFormData({
      nome: conta.nome,
      tipo: conta.tipo,
      saldo_inicial: saldoFormatado,
      cor: conta.cor || '#3B82F6',
      icone: conta.icone || '',
      limite_credito: limiteFormatado,
      dia_fechamento: conta.dia_fechamento || null,
      dia_vencimento: conta.dia_vencimento || null
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'conta_corrente',
      saldo_inicial: '0,00',
      cor: '#3B82F6',
      icone: '',
      limite_credito: '0,00',
      dia_fechamento: null,
      dia_vencimento: null
    });
  };

  const getTipoLabel = (tipo) => {
    const found = TIPOS_CONTA.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  const getTipoIcon = (tipo) => {
    const found = TIPOS_CONTA.find(t => t.value === tipo);
    return found ? found.Icon : CreditCard;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={onVoltar}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'transparent',
            border: `1px solid ${themeColors.border}`,
            borderRadius: '8px',
            color: themeColors.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          <ArrowLeft size={18} />
          Voltar
        </button>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: themeColors.text, marginBottom: '8px' }}>
          Contas
        </h1>
        <p style={{ color: themeColors.textSecondary, fontSize: '14px' }}>
          Gerencie suas contas bancárias, cartões e investimentos
        </p>
      </div>

      {/* Add Button */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            setEditingConta(null);
            resetForm();
            setShowModal(true);
          }}
          style={{
            padding: '10px 24px',
            background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
          }}
        >
          <Plus size={18} />
          Nova Conta
        </button>
      </div>

      {/* Contas List - Table Style */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: themeColors.textSecondary }}>Carregando...</p>
        </div>
      ) : contas.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          background: isDarkMode ? '#252b3b' : themeColors.bgCard,
          border: `1px solid ${themeColors.border}`,
          borderRadius: '12px'
        }}>
          <Wallet size={48} style={{ color: themeColors.textSecondary, marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', color: themeColors.textSecondary, marginBottom: '8px' }}>
            Nenhuma conta encontrada
          </p>
          <p style={{ fontSize: '14px', color: themeColors.textSecondary }}>
            Adicione suas contas bancárias, cartões e investimentos
          </p>
        </div>
      ) : (
        <div style={{
          background: isDarkMode ? '#252b3b' : themeColors.bgCard,
          border: `1px solid ${themeColors.border}`,
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 200px 150px 150px 120px 100px',
            padding: '16px 20px',
            background: isDarkMode ? 'rgba(6, 182, 212, 0.05)' : 'rgba(6, 182, 212, 0.03)',
            borderBottom: `1px solid ${themeColors.border}`,
            fontWeight: '600',
            fontSize: '13px',
            color: themeColors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <div>Cor</div>
            <div>Nome</div>
            <div>Tipo</div>
            <div>Saldo/Limite</div>
            <div>Fechamento</div>
            <div>Vencimento</div>
            <div style={{ textAlign: 'right' }}>Ações</div>
          </div>

          {/* Table Body */}
          {contas.map((conta, index) => {
            const isCartaoCredito = conta.tipo === 'cartao_credito';
            
            return (
              <div
                key={conta.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 200px 150px 150px 120px 100px',
                  padding: '16px 20px',
                  borderBottom: index < contas.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(6, 182, 212, 0.05)' : 'rgba(6, 182, 212, 0.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {/* Color Circle */}
                <div>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: conta.cor || '#3B82F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.createElement(getTipoIcon(conta.tipo), { size: 20, style: { color: 'white' } })}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <h3 style={{ 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    color: themeColors.text,
                    margin: 0
                  }}>
                    {conta.nome}
                  </h3>
                </div>

                {/* Type */}
                <div>
                  <span style={{ 
                    fontSize: '14px', 
                    color: themeColors.textSecondary,
                    fontWeight: '500'
                  }}>
                    {getTipoLabel(conta.tipo)}
                  </span>
                </div>

                {/* Saldo/Limite */}
                <div>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: isCartaoCredito ? '#8B5CF6' : '#10B981'
                  }}>
                    {formatCurrency(isCartaoCredito ? (conta.limite_credito || 0) : (conta.saldo_inicial || 0))}
                  </span>
                  {isCartaoCredito && (
                    <div style={{ fontSize: '11px', color: themeColors.textSecondary, marginTop: '2px' }}>
                      Limite
                    </div>
                  )}
                </div>

                {/* Fechamento */}
                <div>
                  {isCartaoCredito && conta.dia_fechamento ? (
                    <span style={{ 
                      fontSize: '14px', 
                      color: themeColors.text,
                      fontWeight: '500'
                    }}>
                      Dia {conta.dia_fechamento}
                    </span>
                  ) : (
                    <span style={{ fontSize: '14px', color: themeColors.textSecondary }}>-</span>
                  )}
                </div>

                {/* Vencimento */}
                <div>
                  {isCartaoCredito && conta.dia_vencimento ? (
                    <span style={{ 
                      fontSize: '14px', 
                      color: themeColors.text,
                      fontWeight: '500'
                    }}>
                      Dia {conta.dia_vencimento}
                    </span>
                  ) : (
                    <span style={{ fontSize: '14px', color: themeColors.textSecondary }}>-</span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => openEditModal(conta)}
                  style={{
                    padding: '6px',
                    background: 'transparent',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: themeColors.text,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title="Editar conta"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => handleDelete(conta)}
                  style={{
                    padding: '6px',
                    background: 'transparent',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title="Excluir conta"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div 
          onMouseDown={(e) => {
            // Se clique começou no backdrop, marcar para fechar
            if (e.target === e.currentTarget) {
              e.currentTarget.dataset.closeOnMouseUp = 'true';
            }
          }}
          onMouseUp={(e) => {
            // Só fechar se mousedown E mouseup foram no backdrop
            if (e.target === e.currentTarget && e.currentTarget.dataset.closeOnMouseUp === 'true') {
              handleCloseModal();
            }
            delete e.currentTarget.dataset.closeOnMouseUp;
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div 
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: isDarkMode ? '#252b3b' : 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '90%',
              maxWidth: '500px'
            }}
          >
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: themeColors.text,
              marginBottom: '24px'
            }}>
              {editingConta ? 'Editar Conta' : 'Nova Conta'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text,
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Nome da Conta *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Nubank, Itaú, C6 Bank..."
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text,
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Tipo de Conta *
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {TIPOS_CONTA.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Saldo Inicial - apenas para contas que não são cartão de crédito */}
              {formData.tipo !== 'cartao_credito' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: themeColors.text,
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    Saldo Inicial
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: themeColors.accent,
                      fontSize: '14px',
                      fontWeight: '600',
                      pointerEvents: 'none'
                    }}>
                      R$
                    </span>
                    <input
                      type="text"
                      value={formData.saldo_inicial}
                      onChange={handleSaldoInicialChange}
                      placeholder="0,00"
                      style={{
                        width: '100%',
                        padding: '12px 12px 12px 45px',
                        borderRadius: '8px',
                        border: `2px solid ${themeColors.border}`,
                        background: isDarkMode ? '#1a1f2e' : 'white',
                        color: themeColors.text,
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Campos específicos para Cartão de Crédito */}
              {formData.tipo === 'cartao_credito' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: themeColors.text,
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      Limite do Cartão *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: themeColors.accent,
                        fontSize: '14px',
                        fontWeight: '600',
                        pointerEvents: 'none'
                      }}>
                        R$
                      </span>
                      <input
                        type="text"
                        value={formData.limite_credito}
                        onChange={handleLimiteCreditoChange}
                        placeholder="0,00"
                        required
                        style={{
                          width: '100%',
                          padding: '12px 12px 12px 45px',
                          borderRadius: '8px',
                          border: `2px solid ${themeColors.border}`,
                          background: isDarkMode ? '#1a1f2e' : 'white',
                          color: themeColors.text,
                          fontSize: '14px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: themeColors.text,
                        display: 'block',
                        marginBottom: '8px'
                      }}>
                        Dia Fechamento *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.dia_fechamento || ''}
                        onChange={(e) => setFormData({ ...formData, dia_fechamento: parseInt(e.target.value) || null })}
                        placeholder="Ex: 10"
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: `2px solid ${themeColors.border}`,
                          background: isDarkMode ? '#1a1f2e' : 'white',
                          color: themeColors.text,
                          fontSize: '14px',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: themeColors.text,
                        display: 'block',
                        marginBottom: '8px'
                      }}>
                        Dia Vencimento *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.dia_vencimento || ''}
                        onChange={(e) => setFormData({ ...formData, dia_vencimento: parseInt(e.target.value) || null })}
                        placeholder="Ex: 17"
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: `2px solid ${themeColors.border}`,
                          background: isDarkMode ? '#1a1f2e' : 'white',
                          color: themeColors.text,
                          fontSize: '14px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                    border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                    marginBottom: '16px'
                  }}>
                    <p style={{ fontSize: '12px', color: themeColors.textSecondary, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Lightbulb size={14} style={{ flexShrink: 0, color: '#3B82F6' }} />
                      <span>
                        <strong>Dica:</strong> O dia de fechamento é quando a fatura fecha (ex: dia 10). 
                        O dia de vencimento é quando você deve pagar (ex: dia 17).
                      </span>
                    </p>
                  </div>
                </>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text,
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Cor da Conta
                </label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {CORES_DISPONIVEIS.map(cor => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setFormData({ ...formData, cor })}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: cor,
                        border: formData.cor === cor ? `2px solid ${themeColors.accent}` : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: formData.cor === cor ? '0 0 0 2px rgba(6, 182, 212, 0.2)' : 'none',
                        transform: formData.cor === cor ? 'scale(1.05)' : 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.cor !== cor) {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.cor !== cor) {
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingConta(null);
                    resetForm();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    border: `2px solid ${themeColors.border}`,
                    borderRadius: '10px',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 32px',
                    background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
                  }}
                >
                  {editingConta ? 'Salvar Alterações' : 'Criar Conta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContasView;
